import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { loadConnectAndInitialize } from '@stripe/connect-js';
import { BaseComponent } from 'src/app/core/base.component';
import { AccountService } from 'src/app/account/account.service';
import { AlertModalComponent } from 'src/app/shared/modals/alert-modal/alert-modal.component';
import { StripeConnectExpressCreateModalComponent } from 'src/app/shared/modals/stripe-connect-express-create-modal/stripe-connect-express-create-modal.component';
import { CreditCardFeeType } from 'src/app/shared/models/creditcard-fee-type.enum';
import { GolfTechType } from 'src/app/shared/models/golftech-type.enum';
import { OrganizationService } from 'src/app/shared/services/identity/organization.service';
import { SessionService } from 'src/app/shared/services/session.service';
import { StringHelpers } from 'src/app/shared/helpers/string-helpers';
import { GtStripeService } from 'src/app/shared/services/gt-stripe.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-my-account',
  templateUrl: './my-account.component.html',
  styleUrls: ['./my-account.component.scss'],
  standalone: false
})
export class MyAccountComponent extends BaseComponent implements OnInit, OnDestroy {
  private destroyed$ = new Subject<void>();

  CreditCardFeeType = CreditCardFeeType;
  GolfTechType = GolfTechType;
  id: string;
  venmoQrFileName: string;

  userForm = new FormGroup({
    firstName: new FormControl('', Validators.required),
    lastName: new FormControl('', Validators.required),
    email: new FormControl('', [Validators.required, Validators.email]),
    phoneNumber: new FormControl('', Validators.required),
    golfTechType: new FormControl<GolfTechType | null>(null, Validators.required),
    venmoUsername: new FormControl(''),
    venmoQrCode: new FormControl('')
  });

  organizationForm = new FormGroup({
    organizationName: new FormControl('', Validators.required),
    widgetApiKey: new FormControl({ value: '', disabled: true }),
    creditCardFeeType: new FormControl(CreditCardFeeType.Fixed),
    creditCardFee: new FormControl(0, [Validators.required, Validators.min(0)]),
    timeZone: new FormControl('America/New_York', Validators.required)
  });

  usTimezones: { value: string; label: string }[] = [];
  additionalEmails: string[] = [];
  private operatingHoursPayload = '[]';

  stripeLoginLink: any = {};
  stripeAccount: any;
  isPreparingOnboarding = false;

  constructor(
    sessionService: SessionService,
    stringHelpers: StringHelpers,
    private accountService: AccountService,
    private organizationService: OrganizationService,
    private route: ActivatedRoute,
    private stripeService: GtStripeService,
    private dialog: MatDialog,
  ) {
    super(sessionService, stringHelpers);
  }

  ngOnInit(): void {
    this.generateUsTimezones();
    this.route.paramMap.pipe(takeUntil(this.destroyed$)).subscribe((params) => {
      this.id = params.get('id');
    });
    this.loadUserData();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private loadUserData(): void {
    this.isLoading = true;
    this.accountService.getUserByAuth0Id(this.sessionService.getUser().auth0Id).subscribe({
      next: (response: any) => {
        const currentUser = this.sessionService.getUser();
        if (JSON.stringify(currentUser) !== JSON.stringify(response)) {
          this.sessionService.setUser(response);
        }

        this.userForm.patchValue({
          firstName: response.firstName,
          lastName: response.lastName,
          email: response.email,
          phoneNumber: response.phoneNumber,
          golfTechType:
            response.golfTechType != null ? (Number(response.golfTechType) as GolfTechType) : null,
          venmoUsername: response.venmoUsername ?? '',
          venmoQrCode: typeof response.venmoQrCode === 'string' ? response.venmoQrCode : ''
        });

        this.organizationForm.patchValue({
          organizationName: response.organizationName,
          widgetApiKey: response.apiKey,
          creditCardFeeType: response.creditCardFeeType != null ? Number(response.creditCardFeeType) : CreditCardFeeType.Fixed,
          creditCardFee: response.creditCardFee != null ? Number(response.creditCardFee) : 0,
          timeZone: response.timeZone || 'America/New_York'
        });
        this.onFeeTypeChange();

        this.additionalEmails = response.additionalEmails
          ? response.additionalEmails.split(',').map((e: string) => e.trim()).filter(Boolean)
          : [];
        this.operatingHoursPayload =
          typeof response.operatingHours === 'string'
            ? response.operatingHours || '[]'
            : JSON.stringify(response.operatingHours ?? []);

        if (this.sessionService.getUser().stripeAccountId) {
          this.stripeService.getAccountById(this.sessionService.getUser().stripeAccountId).subscribe({
            next: (acct: any) => {
              this.stripeAccount = acct;
              const pastDue = this.stripeAccount?.requirements?.past_due?.length ?? 0;
              if (pastDue === 0 && this.stripeAccount.type === 'express') {
                this.getStripeAccountLoginLink();
              } else {
                this.stripeLoginLink = { url: 'https://dashboard.stripe.com/' };
                this.isLoading = false;
              }
            },
            error: () => {
              this.isLoading = false;
            }
          });
        } else {
          this.isLoading = false;
        }
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  get isFeePercentage(): boolean {
    return this.organizationForm.get('creditCardFeeType').value === CreditCardFeeType.Percentage;
  }

  onVenmoQrSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (file) {
      this.venmoQrFileName = file.name;
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        const base64 = result.includes(',') ? result.split(',')[1] : result;
        this.userForm.get('venmoQrCode').setValue(base64);
      };
      reader.readAsDataURL(file);
    }
  }

  onFeeTypeChange(): void {
    const feeControl = this.organizationForm.get('creditCardFee');
    if (this.isFeePercentage) {
      feeControl.setValidators([Validators.required, Validators.min(0), Validators.max(100)]);
    } else {
      feeControl.setValidators([Validators.required, Validators.min(0)]);
    }
    feeControl.updateValueAndValidity();
  }

  onUserInfoSubmit(): void {
    if (!this.userForm.valid) {
      this.userForm.markAllAsTouched();
      return;
    }
    this.isLoading = true;
    const data = new FormData();
    data.append('id', this.sessionService.getUser().id);
    data.append('firstName', this.userForm.get('firstName').value);
    data.append('lastName', this.userForm.get('lastName').value);
    data.append('email', this.userForm.get('email').value);
    data.append('phoneNumber', this.userForm.get('phoneNumber').value);
    data.append('golfTechType', String(this.userForm.get('golfTechType').value));
    data.append('venmoUsername', this.userForm.get('venmoUsername').value ?? '');
    data.append('venmoQrCode', (this.userForm.get('venmoQrCode').value as string) ?? '');
    data.append('lastEditedBy', this.sessionService.getUser().id);

    this.accountService.updateUser(this.sessionService.getUser().id, data).subscribe({
      next: () => {
        this.loadUserData();
        this.isLoading = false;
      },
      error: (error) => {
        console.error(error);
        this.setError(this.userForm, error);
        this.isLoading = false;
      },
      complete: () => {
        this.isLoading = false;
      }
    });
  }

  onOrganizationSubmit(): void {
    if (!this.organizationForm.valid) {
      this.organizationForm.markAllAsTouched();
      return;
    }
    this.isLoading = true;
    const u = this.sessionService.getUser();
    const data = {
      id: u.organizationId,
      widgetApiKey: this.organizationForm.get('widgetApiKey').value,
      organizationName: this.organizationForm.get('organizationName').value,
      creditCardFee: this.organizationForm.get('creditCardFee').value,
      creditCardFeeType: this.organizationForm.get('creditCardFeeType').value,
      timezone: this.organizationForm.get('timeZone').value,
      lastEditedBy: u.id,
      stripeAccountId: u.stripeAccountId,
      additionalEmails: this.additionalEmails,
      operatingHours: this.operatingHoursPayload
    };

    this.organizationService.update(data.id, data).subscribe({
      next: (response: any) => {
        if (response?.additionalEmails !== undefined) {
          if (Array.isArray(response.additionalEmails)) {
            this.additionalEmails = response.additionalEmails;
          } else if (typeof response.additionalEmails === 'string') {
            this.additionalEmails = response.additionalEmails.split(',').map((e: string) => e.trim()).filter(Boolean);
          }
        }
        if (response?.operatingHours) {
          this.operatingHoursPayload =
            typeof response.operatingHours === 'string'
              ? response.operatingHours
              : JSON.stringify(response.operatingHours);
        }
        this.loadUserData();
        this.isLoading = false;
      },
      error: (error) => {
        console.error(error);
        this.setError(this.organizationForm, error);
        this.isLoading = false;
      }
    });
  }

  regenerateApiKey(): void {
    const dialogRef = this.dialog.open(AlertModalComponent, {
      width: 'auto',
      height: 'auto',
    });

    dialogRef.componentInstance.title = 'Warning';
    dialogRef.componentInstance.body =
      'Are you sure you want to regenerate the API key? This action cannot be undone and will effect the booking functionality on your website. <br /><br />Please re-enter the iFrame with the updated api key on your website to avoid loosing booking functionality.';
    dialogRef.componentInstance.submitText = 'Yes';
    dialogRef.componentInstance.otherText = 'Cancel';
    dialogRef.componentInstance.isOtherTextDisabled = false;

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.toLowerCase() === 'yes') {
        this.accountService.regenerateApiKey().subscribe({
          next: (apiKey: string) => {
            const dialogRef2 = this.dialog.open(AlertModalComponent, {
              width: 'auto',
              height: 'auto',
            });

            dialogRef2.componentInstance.title = 'New API Key';
            dialogRef2.componentInstance.body =
              'Do you want to save your new API Key? <br /><br />Your new API Key is: <strong>' + apiKey + '</strong>';
            dialogRef2.componentInstance.submitText = 'Yes';
            dialogRef2.componentInstance.otherText = 'No';
            dialogRef2.componentInstance.isOtherTextDisabled = false;

            dialogRef2.afterClosed().subscribe((onSaveResult) => {
              if (onSaveResult && onSaveResult.toLowerCase() === 'yes') {
                this.organizationForm.get('widgetApiKey').setValue(apiKey);
                this.onOrganizationSubmit();
              }
            });
          },
          error: (error) => {
            this.organizationForm.get('widgetApiKey').setErrors({ server: 'Unable to regenerate API Key' });
            console.error(error);
          },
          complete: () => {
            this.isLoading = false;
          }
        });
      }
    });
  }

  getStripeAccountLoginLink(): void {
    this.stripeService.getAccountLoginLink(this.getUserFromSession().stripeAccountId).subscribe({
      next: (result: any) => {
        this.stripeLoginLink = result;
      },
      error: (err) => {
        this.isLoading = false;
        console.log(err);
      },
      complete: () => {
        this.isLoading = false;
      }
    });
  }

  getAccountLink(): void {
    this.isPreparingOnboarding = true;

    const fetchClientSecret = async () => {
      return new Promise<string>((resolve, reject) => {
        this.stripeService.createAccountSession(this.getUserFromSession().stripeAccountId).subscribe({
          next: (result: any) => {
            resolve(result.client_secret);
          },
          error: (err) => {
            console.error('Error fetching client secret:', err);
            reject(err);
          }
        });
      });
    };

    const stripeConnectInstance = loadConnectAndInitialize({
      publishableKey: environment.stripe.token,
      fetchClientSecret: fetchClientSecret,
      appearance: {
        overlays: 'dialog'
      },
    });

    const accountOnboarding = stripeConnectInstance.create('account-onboarding');

    accountOnboarding.setOnExit(() => {
      console.log('User exited the onboarding flow');
      window.location.reload();
    });

    accountOnboarding.setOnStepChange((stepChange: any) => {
      console.log('User navigated to step: ' + stepChange.step);
      if (this.isPreparingOnboarding) {
        this.isPreparingOnboarding = false;
      }
    });

    accountOnboarding.setFullTermsOfServiceUrl('https://www.golftechmgt.com/terms-of-service');
    accountOnboarding.setPrivacyPolicyUrl('https://www.golftechmgt.com/privacy-policy');
    accountOnboarding.setCollectionOptions({
      fields: 'currently_due',
      futureRequirements: 'include',
    });

    const container = document.getElementById('connect-container-outing');
    if (container) {
      if (!container.hasChildNodes()) {
        container.appendChild(accountOnboarding);
      } else {
        container.removeChild(container.firstChild as Node);
        container.appendChild(accountOnboarding);
      }
    }

    customElements.whenDefined('stripe-connect-account-onboarding').then(() => {
      const c = document.getElementById('connect-container-outing');
      if (!c) {
        return;
      }
      const observer = new MutationObserver((_mutations, obs) => {
        const iframe = c.querySelector('iframe');

        if (iframe) {
          console.log('Stripe fully initialized');
          obs.disconnect();
          this.isPreparingOnboarding = false;
        }
      });

      observer.observe(c, { childList: true, subtree: true });
    });
  }

  onboard(): void {
    const dialogRef = this.dialog.open(StripeConnectExpressCreateModalComponent, {
      width: '35vw',
      disableClose: true
    });

    dialogRef.componentInstance.disableClose = false;
    const accountBaseUrl = environment.dashboardLocation + '/my-account/' + this.getUserIdFromSession();
    dialogRef.componentInstance.businessDetailsForm.get('golfTechId').patchValue(this.getUserIdFromSession());
    dialogRef.componentInstance.businessDetailsForm.get('refreshUrl').patchValue(accountBaseUrl);
    dialogRef.componentInstance.businessDetailsForm.get('returnUrl').patchValue(accountBaseUrl);
    dialogRef.componentInstance.businessDetailsForm.get('emailAddress').patchValue(this.getUserFromSession().email);
    dialogRef.componentInstance.businessDetailsForm.get('name').patchValue(
      this.getUserFromSession().firstName + ' ' + this.getUserFromSession().lastName);

    dialogRef.afterClosed().subscribe({
      next: (result) => this.onStripeOnboardModalClosed(result),
      error: (err) => {
        console.log(err);
        this.isPreparingOnboarding = false;
      }
    });
  }

  private onStripeOnboardModalClosed(result: unknown): void {
    this.isPreparingOnboarding = true;
    if (this.getUserFromSession().stripeAccountId !== undefined && this.getUserFromSession().stripeAccountId !== null) {
      return;
    }
    this.stripeService.onboardStripeConnectAccount(result).subscribe({
      next: (stripeResult: any) => {
        if (!stripeResult) {
          return;
        }
        const successDialog = this.dialog.open(AlertModalComponent, {
          width: '35vw',
          disableClose: true
        });

        const user = this.sessionService.getUser();
        user.stripeAccountId = stripeResult.id;
        this.sessionService.setUser(user);

        successDialog.disableClose = true;
        successDialog.componentInstance.title = 'Stripe Account Created Successfully';
        successDialog.componentInstance.body = 'Please click okay to finish the Stripe Connect Account setup to start accepting payments from customers.';
        successDialog.componentInstance.submitText = 'Okay';

        successDialog.afterClosed().subscribe(() => {
          this.isPreparingOnboarding = false;
          this.getAccountLink();
        });
      },
      error: (err) => {
        console.log(err);
        this.isPreparingOnboarding = false;
      }
    });
  }

  private generateUsTimezones(): void {
    if (typeof Intl !== 'undefined' && 'supportedValuesOf' in Intl) {
      try {
        const allTimezones = Intl.supportedValuesOf('timeZone');
        const usOnly = allTimezones.filter((tz) => this.isUsTimezone(tz));

        this.usTimezones = usOnly
          .map((tz) => {
            const now = new Date();
            const formatter = new Intl.DateTimeFormat('en-US', {
              timeZone: tz,
              timeZoneName: 'long'
            });
            const parts = formatter.formatToParts(now);
            const timeZoneName = parts.find((part) => part.type === 'timeZoneName')?.value || tz;
            const offset = this.getTimezoneOffset(tz, now);
            const label = timeZoneName + ' (' + offset + ') - ' + this.getCityName(tz);
            return { value: tz, label };
          })
          .sort((a, b) => a.label.localeCompare(b.label));
      } catch {
        console.warn('Intl.supportedValuesOf not supported, using fallback');
        this.generateFallbackTimezones();
      }
    } else {
      this.generateFallbackTimezones();
    }
  }

  private isUsTimezone(tzId: string): boolean {
    const usTimezonePatterns = [
      'America/New_York',
      'America/Chicago',
      'America/Denver',
      'America/Los_Angeles',
      'America/Anchorage',
      'America/Phoenix',
      'America/Detroit',
      'America/Indiana/Indianapolis',
      'America/Indiana/Knox',
      'America/Indiana/Marengo',
      'America/Indiana/Petersburg',
      'America/Indiana/Tell_City',
      'America/Indiana/Vevay',
      'America/Indiana/Vincennes',
      'America/Indiana/Winamac',
      'America/Kentucky/Louisville',
      'America/Kentucky/Monticello',
      'America/Menominee',
      'America/Metlakatla',
      'America/Nome',
      'America/Sitka',
      'America/Yakutat',
      'Pacific/Honolulu'
    ];
    return usTimezonePatterns.includes(tzId);
  }

  private generateFallbackTimezones(): void {
    this.usTimezones = [
      { value: 'America/New_York', label: 'Eastern Time (ET) - New York, Miami, Atlanta' },
      { value: 'America/Chicago', label: 'Central Time (CT) - Chicago, Dallas, Houston' },
      { value: 'America/Denver', label: 'Mountain Time (MT) - Denver, Phoenix, Salt Lake City' },
      { value: 'America/Los_Angeles', label: 'Pacific Time (PT) - Los Angeles, San Francisco, Seattle' },
      { value: 'America/Anchorage', label: 'Alaska Time (AKT) - Anchorage, Juneau' },
      { value: 'Pacific/Honolulu', label: 'Hawaii Time (HST) - Honolulu' },
      { value: 'America/Phoenix', label: 'Arizona Time (MST) - Phoenix (No DST)' }
    ];
  }

  private getTimezoneOffset(tzId: string, date: Date): string {
    try {
      const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
      const tzOpts: Intl.DateTimeFormatOptions = { timeZone: tzId };
      const tzDate = new Date(date.toLocaleString('en-US', tzOpts));
      const offset = (tzDate.getTime() - utcDate.getTime()) / (1000 * 60 * 60);
      const sign = offset >= 0 ? ' +' : ' -';
      const hours = Math.abs(Math.floor(offset));
      const minutes = Math.abs((offset % 1) * 60);
      const h = hours.toString().padStart(2, '0');
      const m = minutes.toString().padStart(2, '0');
      return 'UTC' + sign + h + ':' + m;
    } catch {
      return '';
    }
  }

  private getCityName(tzId: string): string {
    const parts = tzId.split('/');
    const cityPart = parts[parts.length - 1];
    return cityPart
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }
}
